import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  LinkIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentDuplicateIcon,
  UserIcon,
  FunnelIcon,
  ArrowTopRightOnSquareIcon,
  GlobeAltIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { apiHelpers } from '../../services/api';
import toast from 'react-hot-toast';

const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DATE_PRESETS = [
  { id: 'today', label: 'Today', getDates: () => {
    const dateStr = formatLocalDate(new Date());
    return { startDate: dateStr, endDate: dateStr };
  }},
  { id: 'yesterday', label: 'Yesterday', getDates: () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const dateStr = formatLocalDate(yesterday);
    return { startDate: dateStr, endDate: dateStr };
  }},
  { id: 'last7days', label: 'Last 7 Days', getDates: () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 6);
    return { startDate: formatLocalDate(start), endDate: formatLocalDate(end) };
  }},
  { id: 'last30days', label: 'Last 30 Days', getDates: () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 29);
    return { startDate: formatLocalDate(start), endDate: formatLocalDate(end) };
  }},
  { id: 'thisMonth', label: 'This Month', getDates: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { startDate: formatLocalDate(start), endDate: formatLocalDate(end) };
  }},
  { id: 'lastMonth', label: 'Last Month', getDates: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { startDate: formatLocalDate(start), endDate: formatLocalDate(end) };
  }},
  { id: 'thisYear', label: 'This Year', getDates: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    return { startDate: formatLocalDate(start), endDate: formatLocalDate(now) };
  }}
];

function FilterPresetEditor({ chart, values, onChange }) {
  const enabledFilters = chart?.filterConfig?.filter(f => f.enabled) || [];
  const hasDateFilter = chart?.dateFilterConfig?.enabled && chart?.dateFilterConfig?.column;
  const hasFilters = enabledFilters.length > 0 || hasDateFilter;

  const [filterOptions, setFilterOptions] = useState({});
  const [loadingFilterOptions, setLoadingFilterOptions] = useState({});
  const fetchedRef = useRef({});

  useEffect(() => {
    if (!chart?._id) return;
    const selectFilters = enabledFilters.filter(f => f.type === 'select' || f.type === 'multiselect');
    selectFilters.forEach((f) => {
      const key = `${chart._id}_${f.id}`;
      if (fetchedRef.current[key]) return;
      fetchedRef.current[key] = true;
      setLoadingFilterOptions(prev => ({ ...prev, [f.id]: true }));
      apiHelpers.getChartFilterOptions(chart._id, f.id)
        .then(res => {
          setFilterOptions(prev => ({ ...prev, [f.id]: res.data?.options || [] }));
        })
        .catch(err => {
          console.error('[FilterPresetEditor] Failed to fetch options for', f.id, err);
          setFilterOptions(prev => ({ ...prev, [f.id]: [] }));
          fetchedRef.current[`${chart._id}_${f.id}`] = false;
        })
        .finally(() => {
          setLoadingFilterOptions(prev => ({ ...prev, [f.id]: false }));
        });
    });
  }, [chart?._id, enabledFilters]);

  if (!hasFilters) return null;

  const updateFilter = (filterId, value) => {
    const next = { ...values };
    if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
      delete next[filterId];
    } else {
      next[filterId] = value;
    }
    onChange(next);
  };

  return (
    <div className="space-y-4 mt-3 pl-4 border-l-2 border-surface-200 dark:border-surface-600">
      <p className="text-xs font-medium text-surface-600 dark:text-surface-400">Filter presets for: {chart?.name}</p>
      {hasDateFilter && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-surface-600 dark:text-surface-400">Date range</label>
          <div className="flex flex-wrap gap-1">
            {DATE_PRESETS.map((preset) => {
              const isActive = values._dateRange?.preset === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => updateFilter('_dateRange', { preset: preset.id })}
                  className={`px-2 py-1 text-xs rounded ${isActive ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' : 'bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-600'}`}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="date"
              value={values._dateRange?.preset ? '' : (values._dateRange?.startDate || '')}
              onChange={(e) => updateFilter('_dateRange', { startDate: e.target.value, endDate: values._dateRange?.endDate || e.target.value })}
              className="flex-1 px-2 py-1 text-xs rounded border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-700"
              placeholder="Custom start"
            />
            <span className="text-surface-400 text-xs">to</span>
            <input
              type="date"
              value={values._dateRange?.preset ? '' : (values._dateRange?.endDate || '')}
              onChange={(e) => updateFilter('_dateRange', { ...values._dateRange, endDate: e.target.value })}
              className="flex-1 px-2 py-1 text-xs rounded border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-700"
              placeholder="Custom end"
            />
          </div>
          {values._dateRange?.preset && (
            <p className="text-xs text-primary-600 dark:text-primary-400">
              Preset active: <strong>{DATE_PRESETS.find(p => p.id === values._dateRange.preset)?.label}</strong>. Dates are computed dynamically when viewers load the dashboard.
            </p>
          )}
        </div>
      )}
      {enabledFilters.map((filter) => (
        <div key={filter.id} className="space-y-1">
          <label className="text-xs font-medium text-surface-600 dark:text-surface-400">{filter.label}</label>
          {filter.type === 'daterange' && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1">
                {DATE_PRESETS.map((preset) => {
                  const isActive = values[filter.id]?.preset === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => updateFilter(filter.id, { preset: preset.id })}
                      className={`px-2 py-1 text-xs rounded ${isActive ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' : 'bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-600'}`}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2 items-center">
                <input
                  type="date"
                  value={values[filter.id]?.preset ? '' : (values[filter.id]?.startDate || '')}
                  onChange={(e) => updateFilter(filter.id, { startDate: e.target.value, endDate: values[filter.id]?.endDate || e.target.value })}
                  className="flex-1 px-2 py-1 text-xs rounded border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-700"
                  placeholder="Custom start"
                />
                <span className="text-surface-400 text-xs">to</span>
                <input
                  type="date"
                  value={values[filter.id]?.preset ? '' : (values[filter.id]?.endDate || '')}
                  onChange={(e) => updateFilter(filter.id, { ...values[filter.id], endDate: e.target.value })}
                  className="flex-1 px-2 py-1 text-xs rounded border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-700"
                  placeholder="Custom end"
                />
              </div>
              {values[filter.id]?.preset && (
                <p className="text-xs text-primary-600 dark:text-primary-400">
                  Preset active: <strong>{DATE_PRESETS.find(p => p.id === values[filter.id].preset)?.label}</strong>. Dates are computed dynamically when viewers load the dashboard.
                </p>
              )}
            </div>
          )}
          {filter.type === 'text' && (
            <input
              type="text"
              value={values[filter.id] || ''}
              onChange={(e) => updateFilter(filter.id, e.target.value)}
              placeholder={`Filter ${filter.label}...`}
              className="w-full px-2 py-1 text-xs rounded border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-700"
            />
          )}
          {filter.type === 'number' && (
            <div className="flex gap-2 items-center">
              <input
                type="number"
                placeholder="Min"
                value={values[filter.id]?.min ?? ''}
                onChange={(e) => updateFilter(filter.id, { ...values[filter.id], min: e.target.value === '' ? undefined : Number(e.target.value) })}
                className="flex-1 px-2 py-1 text-xs rounded border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-700"
              />
              <span className="text-surface-400">-</span>
              <input
                type="number"
                placeholder="Max"
                value={values[filter.id]?.max ?? ''}
                onChange={(e) => updateFilter(filter.id, { ...values[filter.id], max: e.target.value === '' ? undefined : Number(e.target.value) })}
                className="flex-1 px-2 py-1 text-xs rounded border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-700"
              />
            </div>
          )}
          {filter.type === 'select' && (
            loadingFilterOptions[filter.id] ? (
              <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-surface-400">
                <div className="w-3 h-3 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                Loading options...
              </div>
            ) : (
              <select
                value={values[filter.id] || ''}
                onChange={(e) => updateFilter(filter.id, e.target.value || '')}
                className="w-full px-2 py-1.5 text-xs rounded border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-700"
              >
                <option value="">All</option>
                {(filterOptions[filter.id] || []).map((opt) => (
                  <option key={String(opt)} value={String(opt)}>{String(opt)}</option>
                ))}
              </select>
            )
          )}
          {filter.type === 'multiselect' && (
            loadingFilterOptions[filter.id] ? (
              <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-surface-400">
                <div className="w-3 h-3 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                Loading options...
              </div>
            ) : (
              <div>
                {(filterOptions[filter.id] || []).length > 0 && (
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-surface-400">
                      {(Array.isArray(values[filter.id]) ? values[filter.id].length : 0)} selected
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => updateFilter(filter.id, (filterOptions[filter.id] || []).map(String))}
                        className="text-[10px] text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        All
                      </button>
                      <button
                        type="button"
                        onClick={() => updateFilter(filter.id, '')}
                        className="text-[10px] text-surface-500 hover:underline"
                      >
                        None
                      </button>
                    </div>
                  </div>
                )}
                <div className="max-h-32 overflow-y-auto border border-surface-300 dark:border-surface-600 rounded bg-white dark:bg-surface-700">
                  {(filterOptions[filter.id] || []).length === 0 ? (
                    <p className="px-2 py-1.5 text-xs text-surface-400 italic">No options available</p>
                  ) : (
                    (filterOptions[filter.id] || []).map((opt) => {
                      const strOpt = String(opt);
                      const selected = Array.isArray(values[filter.id]) && values[filter.id].includes(strOpt);
                      return (
                        <label
                          key={strOpt}
                          className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-surface-50 dark:hover:bg-surface-600 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => {
                              const current = Array.isArray(values[filter.id]) ? [...values[filter.id]] : [];
                              if (selected) {
                                updateFilter(filter.id, current.filter(v => v !== strOpt));
                              } else {
                                updateFilter(filter.id, [...current, strOpt]);
                              }
                            }}
                            className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-surface-700 dark:text-surface-300 truncate">{strOpt}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            )
          )}
        </div>
      ))}
    </div>
  );
}

export default function ShareDashboardModal({ isOpen, onClose, dashboardId, charts = [] }) {
  const [limits, setLimits] = useState(null);
  const [shareLinks, setShareLinks] = useState([]);
  const [expandedLinkId, setExpandedLinkId] = useState(null);
  const [expandedCredId, setExpandedCredId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [newCacheTTL, setNewCacheTTL] = useState(86400);
  const [addCredentialLinkId, setAddCredentialLinkId] = useState(null);
  const [addCredUsername, setAddCredUsername] = useState('');
  const [addCredPassword, setAddCredPassword] = useState('');
  const [addCredLabel, setAddCredLabel] = useState('');
  const [editingPresets, setEditingPresets] = useState(null); // { linkId, credId }
  const [editingPublicPresets, setEditingPublicPresets] = useState(null); // linkId
  const [editingSlug, setEditingSlug] = useState({}); // { linkId: value }

  const loadLimits = useCallback(async () => {
    try {
      const res = await apiHelpers.getShareLinkLimits();
      setLimits(res.data);
    } catch (err) {
      console.error('Failed to load share limits:', err);
      toast.error('Failed to load sharing limits');
    }
  }, []);

  const loadShareLinks = useCallback(async () => {
    if (!dashboardId) return;
    try {
      setIsLoading(true);
      const res = await apiHelpers.getShareLinks(dashboardId);
      setShareLinks(res.data.shareLinks || []);
    } catch (err) {
      console.error('Failed to load share links:', err);
      toast.error('Failed to load share links');
    } finally {
      setIsLoading(false);
    }
  }, [dashboardId]);

  useEffect(() => {
    if (isOpen && dashboardId) {
      loadLimits();
      loadShareLinks();
    }
  }, [isOpen, dashboardId, loadLimits, loadShareLinks]);

  const handleCreateLink = async () => {
    if (!dashboardId || !limits?.canShare) return;
    if (limits.remaining !== null && limits.remaining <= 0) {
      toast.error('Share link limit reached');
      return;
    }
    setIsCreating(true);
    try {
      const res = await apiHelpers.createShareLink(dashboardId, {
        slug: newSlug || undefined,
        cacheTTL: newCacheTTL
      });
      const returnedSlug = res.data.shareLink.slug;
      setShareLinks(prev => [...prev, res.data.shareLink]);
      setNewSlug('');
      setNewCacheTTL(limits.cacheTTLOptions?.[0]?.value ?? 86400);
      if (newSlug && returnedSlug !== newSlug) {
        toast(`Your custom slug is taken. We have adjusted it to "${returnedSlug}".`, { icon: 'ℹ️' });
      } else {
        toast.success('Share link created');
      }
      loadLimits();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to create share link');
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateLink = async (linkId, updates) => {
    try {
      const res = await apiHelpers.updateShareLink(dashboardId, linkId, updates);
      setShareLinks(prev => prev.map(l => l._id === linkId ? { ...l, ...res.data.shareLink } : l));
      toast.success('Share link updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
  };

  const handleTogglePublicAccess = async (linkId, enable) => {
    try {
      const res = await apiHelpers.updateShareLink(dashboardId, linkId, { isPublicAccess: enable });
      setShareLinks(prev => prev.map(l => l._id === linkId ? { ...l, ...res.data.shareLink } : l));
      toast.success(enable ? 'Public access enabled' : 'Public access disabled');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
  };

  const handleUpdatePublicFilterPresets = async (linkId, publicFilterPresets) => {
    try {
      const res = await apiHelpers.updateShareLink(dashboardId, linkId, { publicFilterPresets });
      setShareLinks(prev => prev.map(l => l._id === linkId ? { ...l, ...res.data.shareLink } : l));
      toast.success('Public filter presets updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Delete this share link? All credentials will be removed.')) return;
    try {
      await apiHelpers.deleteShareLink(dashboardId, linkId);
      setShareLinks(prev => prev.filter(l => l._id !== linkId));
      toast.success('Share link deleted');
      loadLimits();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleAddCredential = async () => {
    if (!addCredentialLinkId || !addCredUsername?.trim() || !addCredPassword) {
      toast.error('Username and password are required');
      return;
    }
    const link = shareLinks.find(l => l._id === addCredentialLinkId);
    if (limits?.maxCredentialsPerLink === 1 && link?.credentials?.length >= 1) {
      toast.error('Your plan allows only 1 credential per share link');
      return;
    }
    try {
      const res = await apiHelpers.addShareLinkCredential(dashboardId, addCredentialLinkId, {
        username: addCredUsername.trim(),
        password: addCredPassword,
        label: addCredLabel.trim() || undefined
      });
      setShareLinks(prev => prev.map(l => {
        if (l._id !== addCredentialLinkId) return l;
        return { ...l, credentials: [...(l.credentials || []), res.data.credential] };
      }));
      setAddCredentialLinkId(null);
      setAddCredUsername('');
      setAddCredPassword('');
      setAddCredLabel('');
      toast.success('Credential added');
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to add credential');
    }
  };

  const handleUpdateCredential = async (linkId, credId, updates) => {
    try {
      const res = await apiHelpers.updateShareLinkCredential(dashboardId, linkId, credId, updates);
      setShareLinks(prev => prev.map(l => {
        if (l._id !== linkId) return l;
        return {
          ...l,
          credentials: (l.credentials || []).map(c => c._id === credId ? { ...c, ...res.data.credential } : c)
        };
      }));
      toast.success('Credential updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
  };

  const handleDeleteCredential = async (linkId, credId) => {
    if (!window.confirm('Delete this credential?')) return;
    try {
      await apiHelpers.deleteShareLinkCredential(dashboardId, linkId, credId);
      setShareLinks(prev => prev.map(l => {
        if (l._id !== linkId) return l;
        return { ...l, credentials: (l.credentials || []).filter(c => c._id !== credId) };
      }));
      toast.success('Credential deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  };

  const copyPublicUrl = (slug) => {
    const url = `${window.location.origin}/#/public/dashboard/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const chartsWithFilters = charts.filter(c => {
    const hasConfig = c.filterConfig?.some(f => f.enabled);
    const hasDate = c.dateFilterConfig?.enabled;
    return hasConfig || hasDate;
  });

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="card w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-surface-200 dark:border-surface-700">
                  <Dialog.Title className="text-lg font-semibold text-surface-900 dark:text-white flex items-center gap-2">
                    <LinkIcon className="w-5 h-5" />
                    Share Dashboard
                  </Dialog.Title>
                  <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-4 overflow-y-auto flex-1 space-y-6">
                  {limits && (
                    <div className="text-sm text-surface-600 dark:text-surface-400">
                      {limits.canShare ? (
                        limits.limit === null ? (
                          <span>{limits.current} shared dashboard(s)</span>
                        ) : (
                          <span>{limits.current} of {limits.limit} share links used</span>
                        )
                      ) : (
                        <span className="text-amber-600 dark:text-amber-400">Dashboard sharing is not available on your current plan.</span>
                      )}
                    </div>
                  )}

                  {limits?.canShare && (
                    <>
                      <div className="flex flex-wrap gap-3 items-end">
                        <div className="flex-1 min-w-[180px]">
                          <label className="block text-xs font-medium text-surface-600 dark:text-surface-400 mb-1">Slug (optional)</label>
                          <input
                            type="text"
                            value={newSlug}
                            onChange={(e) => setNewSlug(e.target.value.replace(/[^a-z0-9-]/gi, '-').replace(/-+/g, '-').toLowerCase())}
                            placeholder="e.g. sales-q4"
                            className="input w-full"
                          />
                        </div>
                        <div className="w-40">
                          <label className="block text-xs font-medium text-surface-600 dark:text-surface-400 mb-1">Cache TTL</label>
                          <select
                            value={newCacheTTL}
                            onChange={(e) => setNewCacheTTL(Number(e.target.value))}
                            className="input w-full"
                          >
                            {(limits.cacheTTLOptions || []).map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <button
                          onClick={handleCreateLink}
                          disabled={isCreating || (limits.remaining !== null && limits.remaining <= 0)}
                          className="btn-primary"
                        >
                          {isCreating ? 'Creating...' : 'Create Share Link'}
                        </button>
                      </div>

                      {isLoading ? (
                        <div className="py-8 text-center text-surface-500">Loading...</div>
                      ) : shareLinks.length === 0 ? (
                        <div className="py-8 text-center text-surface-500 border border-dashed border-surface-300 dark:border-surface-600 rounded-lg">
                          No share links yet. Create one above.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {shareLinks.map((link) => (
                            <div key={link._id} className="border border-surface-200 dark:border-surface-700 rounded-lg overflow-hidden">
                              <div
                                className="flex items-center justify-between p-3 cursor-pointer hover:bg-surface-50 dark:hover:bg-surface-800/50"
                                onClick={() => setExpandedLinkId(expandedLinkId === link._id ? null : link._id)}
                              >
                                <div className="flex items-center gap-2">
                                  {expandedLinkId === link._id ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
                                  <span className="font-medium">{link.slug}</span>
                                  {link.isPublicAccess ? (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                      <GlobeAltIcon className="w-3 h-3" /> Public
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-400">
                                      <LockClosedIcon className="w-3 h-3" /> Private
                                    </span>
                                  )}
                                  {!link.isActive && <span className="text-xs text-amber-600">(inactive)</span>}
                                </div>
                                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                  <button
                                    onClick={() => copyPublicUrl(link.slug)}
                                    className="p-2 rounded hover:bg-surface-200 dark:hover:bg-surface-700"
                                    title="Copy link"
                                  >
                                    <DocumentDuplicateIcon className="w-4 h-4" />
                                  </button>
                                  <a
                                    href={`/#/public/dashboard/${link.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 rounded hover:bg-surface-200 dark:hover:bg-surface-700"
                                    title="Open in new tab"
                                  >
                                    <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                                  </a>
                                  <button
                                    onClick={() => handleDeleteLink(link._id)}
                                    className="p-2 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600"
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              {expandedLinkId === link._id && (
                                <div className="p-3 pt-0 border-t border-surface-200 dark:border-surface-700 space-y-4">
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-medium text-surface-500 mb-1">Slug</label>
                                      <input
                                        type="text"
                                        value={editingSlug[link._id] !== undefined ? editingSlug[link._id] : link.slug}
                                        onChange={(e) => setEditingSlug(prev => ({ ...prev, [link._id]: e.target.value.replace(/[^a-z0-9-]/gi, '-').replace(/-+/g, '-').toLowerCase() }))}
                                        onBlur={(e) => {
                                          const val = e.target.value.trim();
                                          if (val && val !== link.slug) handleUpdateLink(link._id, { slug: val });
                                          setEditingSlug(prev => ({ ...prev, [link._id]: undefined }));
                                        }}
                                        className="input w-full text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-surface-500 mb-1">Cache TTL</label>
                                      <select
                                        value={link.cacheTTL}
                                        onChange={(e) => handleUpdateLink(link._id, { cacheTTL: Number(e.target.value) })}
                                        className="input w-full text-sm"
                                      >
                                        {(limits.cacheTTLOptions || []).map(opt => (
                                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        id={`active-${link._id}`}
                                        checked={link.isActive}
                                        onChange={(e) => handleUpdateLink(link._id, { isActive: e.target.checked })}
                                      />
                                      <label htmlFor={`active-${link._id}`} className="text-sm">Active</label>
                                    </div>
                                  </div>

                                  {/* Access Mode: Public or Private (mutually exclusive) */}
                                  {limits?.canHavePublicAccess && (
                                    <div className="border border-surface-200 dark:border-surface-700 rounded-lg p-3 space-y-3">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <GlobeAltIcon className="w-4 h-4 text-surface-500" />
                                          <span className="text-sm font-medium">Public Access</span>
                                          <span className="text-[10px] text-surface-400">Anyone with the link can view</span>
                                        </div>
                                        <button
                                          onClick={() => {
                                            if (!link.isPublicAccess && (link.credentials?.length || 0) > 0) {
                                              if (!window.confirm('Enabling public access will remove all credentials from this link. Continue?')) return;
                                            }
                                            handleTogglePublicAccess(link._id, !link.isPublicAccess);
                                          }}
                                          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                            link.isPublicAccess ? 'bg-green-500' : 'bg-surface-300 dark:bg-surface-600'
                                          }`}
                                        >
                                          <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${link.isPublicAccess ? 'translate-x-4' : 'translate-x-0'}`} />
                                        </button>
                                      </div>
                                      {link.isPublicAccess && (
                                        <p className="text-xs text-surface-500 dark:text-surface-400">
                                          This link is publicly accessible. No login required. Create a separate link for credential-based access.
                                        </p>
                                      )}
                                      {link.isPublicAccess && chartsWithFilters.length > 0 && (
                                        <div>
                                          <button
                                            onClick={() => setEditingPublicPresets(editingPublicPresets === link._id ? null : link._id)}
                                            className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                                          >
                                            <FunnelIcon className="w-3.5 h-3.5" /> {editingPublicPresets === link._id ? 'Hide' : 'Edit'} public link filters
                                          </button>
                                          {editingPublicPresets === link._id && (
                                            <div className="mt-3 space-y-3">
                                              {chartsWithFilters.map(chart => (
                                                <FilterPresetEditor
                                                  key={chart._id}
                                                  chart={chart}
                                                  values={(link.publicFilterPresets?.find(p => (p.chartId?.toString?.() || p.chartId) === (chart._id?.toString?.() || chart._id))?.filters) || {}}
                                                  onChange={(filters) => {
                                                    const presets = [...(link.publicFilterPresets || [])];
                                                    const chartIdStr = chart._id?.toString?.() || chart._id;
                                                    const idx = presets.findIndex(p => (p.chartId?.toString?.() || p.chartId) === chartIdStr);
                                                    const entry = { chartId: chart._id, filters };
                                                    if (idx >= 0) presets[idx] = entry;
                                                    else presets.push(entry);
                                                    handleUpdatePublicFilterPresets(link._id, presets);
                                                  }}
                                                />
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Credentials Section - only shown for private links */}
                                  {!link.isPublicAccess && (
                                    <div>
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                          <LockClosedIcon className="w-4 h-4 text-surface-500" />
                                          <span className="text-sm font-medium">Credentials</span>
                                        </div>
                                        {(() => {
                                          const proMaxReached = limits.maxCredentialsPerLink === 1 && (link.credentials?.length || 0) >= 1;
                                          if (proMaxReached) {
                                            return <span className="text-xs text-amber-600">1 credential max on your plan. Upgrade for more.</span>;
                                          }
                                          return addCredentialLinkId === link._id ? (
                                            <div className="flex gap-2 items-center flex-wrap">
                                              <input
                                                placeholder="Username"
                                                value={addCredUsername}
                                                onChange={(e) => setAddCredUsername(e.target.value)}
                                                className="input text-sm w-28"
                                              />
                                              <input
                                                type="password"
                                                placeholder="Password"
                                                value={addCredPassword}
                                                onChange={(e) => setAddCredPassword(e.target.value)}
                                                className="input text-sm w-28"
                                              />
                                              <input
                                                placeholder="Label (optional)"
                                                value={addCredLabel}
                                                onChange={(e) => setAddCredLabel(e.target.value)}
                                                className="input text-sm w-24"
                                              />
                                              <button onClick={handleAddCredential} className="btn-primary text-sm py-1">Add</button>
                                              <button onClick={() => setAddCredentialLinkId(null)} className="btn-secondary text-sm py-1">Cancel</button>
                                            </div>
                                          ) : (
                                            <button
                                              onClick={() => setAddCredentialLinkId(link._id)}
                                              className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                                            >
                                              <PlusIcon className="w-4 h-4" /> Add Credential
                                            </button>
                                          );
                                        })()}
                                      </div>
                                      {(link.credentials || []).length === 0 && (
                                        <p className="text-xs text-surface-400 italic py-2">No credentials yet. Add one above, or enable public access.</p>
                                      )}
                                      <div className="space-y-2">
                                        {(link.credentials || []).map((cred) => (
                                          <div key={cred._id} className="pl-4 border-l-2 border-surface-200 dark:border-surface-600">
                                            <div className="flex items-center justify-between">
                                              <div className="flex items-center gap-2">
                                                <UserIcon className="w-4 h-4 text-surface-500" />
                                                <span className="text-sm font-medium">{cred.username}</span>
                                                {cred.label && <span className="text-xs text-surface-500">({cred.label})</span>}
                                              </div>
                                              <div className="flex items-center gap-1">
                                                {chartsWithFilters.length > 0 && (
                                                  <button
                                                    onClick={() => setEditingPresets(editingPresets?.credId === cred._id ? null : { linkId: link._id, credId: cred._id })}
                                                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                                                  >
                                                    <FunnelIcon className="w-3.5 h-3.5" /> Filters
                                                  </button>
                                                )}
                                                <button
                                                  onClick={() => handleDeleteCredential(link._id, cred._id)}
                                                  className="text-xs text-red-600 hover:underline"
                                                >
                                                  Delete
                                                </button>
                                              </div>
                                            </div>
                                            {editingPresets?.linkId === link._id && editingPresets?.credId === cred._id && (
                                              <div className="mt-3 space-y-3">
                                                {chartsWithFilters.map(chart => (
                                                  <FilterPresetEditor
                                                    key={chart._id}
                                                    chart={chart}
                                                    values={(cred.filterPresets?.find(p => (p.chartId?.toString?.() || p.chartId) === (chart._id?.toString?.() || chart._id))?.filters) || {}}
                                                    onChange={(filters) => {
                                                      const presets = [...(cred.filterPresets || [])];
                                                      const chartIdStr = chart._id?.toString?.() || chart._id;
                                                      const idx = presets.findIndex(p => (p.chartId?.toString?.() || p.chartId) === chartIdStr);
                                                      const entry = { chartId: chart._id, filters };
                                                      if (idx >= 0) presets[idx] = entry;
                                                      else presets.push(entry);
                                                      handleUpdateCredential(link._id, cred._id, { filterPresets: presets });
                                                    }}
                                                  />
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
